import React, { useState, useLayoutEffect, useContext } from 'react';
import { rawTimeZones } from '@vvo/tzdb';

import * as S from './styles';
import SimpleInput from 'components/SimpleInput';
import { SelectInputItemProps } from 'types/SelectInputItemProps';
import SelectInput from 'components/SelectInput';
import { optionsTypeBannerCarousel } from 'utils/optionsInputs';
import Check from 'components/Buttons/Check';
import { formatDateToISO, formatToDateCustom } from 'utils/functions';
import DynamicPageContext from 'context/DynamicPage';
import { DataFormCarroselBannersProps } from 'types/DataFormCarroselBannersProps';

interface DataFormCarouselBannersProps {
	dataForm: DataFormCarroselBannersProps;
	setDataForm: React.Dispatch<
		React.SetStateAction<DataFormCarroselBannersProps>
	>;
	isShow: boolean;
}

export default function DataFormCarouselBanners({
	dataForm,
	setDataForm,
	isShow
}: DataFormCarouselBannersProps) {
	const { moduleInSavingMode, moduleOnPhoneToEdit } =
		useContext(DynamicPageContext);

	const [isShowDateInputs, setIsShowDateInputs] = useState(false);
	const [timeZoneOptions, setTimeZoneOptions] = useState<
		SelectInputItemProps[]
	>([]);

	// Função para converter minutos para formato GMT±X
	const formatGMT = (offsetMinutes: number): string => {
		const hours = Math.floor(offsetMinutes / 60);
		const minutes = Math.abs(offsetMinutes % 60);
		const sign = offsetMinutes >= 0 ? '+' : '-';
		return `GMT${sign}${Math.abs(hours)}${minutes ? `:${minutes}` : ''}`;
	};

	function findOptionOnListById(value: string, list: SelectInputItemProps[]) {
		const itemsList = list.filter((item) => item.id === value);
		if (itemsList.length > 0) {
			return itemsList[0];
		}

		return '';
	}

	// Criando a lista com GMT formatado
	useLayoutEffect(() => {
		const newListTimeZoneOptions: SelectInputItemProps[] = rawTimeZones.map(
			(tz) => ({
				id: tz.name, // Exemplo: "America/Sao_Paulo"
				option: `${tz.name} (${formatGMT(tz.rawOffsetInMinutes)})` // Exemplo: "America/Sao_Paulo (GMT-3)"
			})
		);

		setTimeZoneOptions(newListTimeZoneOptions);
	}, []);

	//reseta os dados de agendamento os inputs não estejam visiaveis
	useLayoutEffect(() => {
		if (!isShowDateInputs) {
			setDataForm((obj) => {
				if (obj.schedule) {
					return {
						...obj,
						schedule: {
							...obj.schedule,
							start: '',
							end: '',
							timezone: '',
							countdown: false
						}
					};
				}

				return obj;
			});
		}
	}, [isShowDateInputs]);

	//se estiver em edição aqui pega os dados do item editado
	useLayoutEffect(() => {
		if (moduleOnPhoneToEdit !== null && moduleInSavingMode === null) {
			setTimeout(() => {
				setDataForm((obj) => ({ ...obj, ...moduleOnPhoneToEdit.data }));
			}, 100);

			if (moduleOnPhoneToEdit?.data?.schedule) {
				setIsShowDateInputs(true);
			}
		}
	}, []);

	return (
		<S.container $isShow={isShow}>
			<S.twoInputs>
				<SimpleInput
					label="Título *"
					initialValue={dataForm.title}
					getValue={(value: string) => {
						setDataForm((obj) => ({ ...obj, title: value }));
					}}
				/>

				<SelectInput
					list={optionsTypeBannerCarousel}
					getValue={(value: SelectInputItemProps | '') => {
						setDataForm((obj) => ({
							...obj,
							type: value === '' ? value : (value?.id as string)
						}));
					}}
					initialValue={
						dataForm.type === ''
							? optionsTypeBannerCarousel[0]
							: findOptionOnListById(
									dataForm.type || '',
									optionsTypeBannerCarousel
								)
					}
					label="Tipo *"
				/>
			</S.twoInputs>

			<S.twoInputs>
				<SimpleInput
					typeInput="number"
					paddingInLineEnd
					label="Padding Top"
					initialValue={dataForm.paddingTop}
					getValue={(value: string) => {
						setDataForm((obj) => ({ ...obj, paddingTop: parseInt(value) }));
					}}
				/>

				<SimpleInput
					typeInput="number"
					paddingInLineEnd
					label="Padding Bottom"
					initialValue={dataForm.paddingBottom}
					getValue={(value: string) => {
						setDataForm((obj) => ({ ...obj, paddingBottom: parseInt(value) }));
					}}
				/>
			</S.twoInputs>

			<Check
				text="Programação"
				isChecked={isShowDateInputs}
				setIsChecked={() => {
					setIsShowDateInputs((obj) => !obj);
				}}
			/>

			{isShowDateInputs && (
				<>
					<SelectInput
						isHide
						list={timeZoneOptions}
						getValue={(value: SelectInputItemProps | '') => {
							setDataForm((obj) => {
								if (obj.schedule) {
									return {
										...obj,
										schedule: {
											...obj.schedule,
											timezone: value === '' ? value : (value?.id as string)
										}
									};
								}
								return { ...obj };
							});
						}}
						initialValue={
							dataForm.schedule && dataForm.schedule.timezone === ''
								? ''
								: findOptionOnListById(
										dataForm.schedule?.timezone || '',
										timeZoneOptions
									)
						}
						label="Selcione o timerzone"
					/>

					<S.twoInputs>
						<SimpleInput
							typeInput="datetime-local"
							label="Ínicio"
							initialValue={
								dataForm.schedule === null
									? ''
									: formatToDateCustom(dataForm.schedule.start)
							}
							getValue={(value: string) => {
								setDataForm((obj) => {
									if (obj.schedule) {
										return {
											...obj,
											schedule: {
												...obj.schedule,
												start: formatDateToISO(value)
											}
										};
									}

									return { ...obj };
								});
							}}
							paddingInLineEnd
						/>

						<SimpleInput
							typeInput="datetime-local"
							label="Fim"
							initialValue={
								dataForm.schedule === null
									? ''
									: formatToDateCustom(dataForm.schedule.end)
							}
							getValue={(value: string) => {
								setDataForm((obj) => {
									if (obj.schedule) {
										return {
											...obj,
											schedule: {
												...obj.schedule,
												end: formatDateToISO(value)
											}
										};
									}

									return { ...obj };
								});
							}}
							paddingInLineEnd
						/>
					</S.twoInputs>

					<Check
						text="Contagem regressiva"
						isChecked={dataForm.schedule?.countdown}
						setIsChecked={() => {
							setDataForm((obj) => {
								if (obj.schedule) {
									return {
										...obj,
										schedule: {
											...obj.schedule,
											countdown: !obj.schedule.countdown
										}
									};
								}
								return { ...obj };
							});
						}}
					/>
				</>
			)}
		</S.container>
	);
}
